import { useCallback } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { IconPlusOutline18 as Plus } from "nucleo-ui-outline-18"

import { defaultAiChatWelcomeMessage } from "@productlane/lib/src/ai-chat-defaults"
import { cn } from "@productlane/lib/src/cn"
import { Button, NewBadge, Select, Switch } from "@productlane/ui"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { CollapsiblePromptTextarea } from "@/components/collapsible-prompt-textarea"
import { toast } from "@/components/toast"
import { docsDefaultExampleQuestions } from "@/constants"
import { analytics } from "@/lib/analytics"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { PortalSettingsCard } from "@/screens/settings/portal-settings-card"
import { PublishPortalCard } from "@/screens/settings/publish-portal-card"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsSection } from "@/screens/settings/settings-switches/settings-section"
import { useActivePortalInstanceStore } from "@/stores/active-portal-instance-store"
import { useDocsTabDialogs } from "@/stores/docs/docs-tab-store"
import { ApiReferenceSettingsRow } from "./api-reference-settings-row"
import {
	DOCS_PROPOSAL_CADENCE_DESCRIPTION,
	DOCS_PROPOSAL_CADENCE_LABEL,
	DOCS_PROPOSAL_CADENCE_OPTIONS,
	isDocsProposalCadence,
} from "./docs-proposal-cadence"
import { DOCS_TAB_STYLE_OPTIONS, useSaveDocsTabStyle } from "./docs-tab-style"
import { NewApiReferenceDialog } from "./new-api-reference-dialog"
import { useDocsCustomPrompt } from "./use-docs-custom-prompt"

export const HelpCenterSettings = () => {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const navigate = useNavigate()
	const workspaceId = workspace?.id
	const saveTabStyle = useSaveDocsTabStyle()
	const [tabs, tabsResult] = useQuery(queries.helpCenterTabs())
	const portalInstanceId = useActivePortalInstanceStore(
		(s) => s.activePortalInstanceId,
	)
	const { setNewApiReferenceOpen } = useDocsTabDialogs()
	// Waits for the server so a workspace that has one never sees the add row flash in and out.
	const tabsLoaded = tabsResult.type === "complete"
	const apiReferenceTab = tabs.find(
		(tab) =>
			tab.kind === "API_REFERENCE" &&
			(tab.portalInstanceId ?? null) === portalInstanceId,
	)

	const savePrompt = useCallback(
		async (value: string) => {
			if (!workspaceId) return false
			const result = await zero.mutate(
				mutators.workspaces.update({
					id: workspaceId,
					docsCustomPrompt: value.trim() || null,
				}),
			).client
			if (result.type !== "success") {
				captureError(result.error, {
					domain: "general",
					action: "save docs custom prompt",
					workspaceId,
				})
				toast.error({
					title: "Your docs instructions weren't saved",
					description: "Try again in a moment.",
				})
				return false
			}
			analytics.track("update-docs-custom-prompt")
			return true
		},
		[workspaceId, zero],
	)

	const saveCadence = useCallback(
		async (value: string) => {
			if (!workspaceId || !isDocsProposalCadence(value)) return
			const result = await zero.mutate(
				mutators.workspaces.update({
					id: workspaceId,
					docsProposalCadence: value,
				}),
			).client
			if (result.type !== "success") {
				captureError(result.error, {
					domain: "general",
					action: "save docs proposal cadence",
					workspaceId,
				})
				toast.error({
					title: "Your draft frequency wasn't saved",
					description: "Try again in a moment.",
				})
				return
			}
			analytics.track("update-docs-proposal-cadence", { value })
		},
		[workspaceId, zero],
	)

	const docsPrompt = useDocsCustomPrompt({
		persisted: workspace?.docsCustomPrompt ?? "",
		save: savePrompt,
	})

	if (!workspace) {
		return null
	}

	const githubConnected = !!workspace.githubSettings?.repositoryFullName
	const dimmedClassName = cn("transition-opacity duration-200 ease-in-out", {
		"opacity-50": !workspace.helpCenterPublic,
	})

	return (
		<>
			<PublishPortalCard
				checked={workspace.helpCenterPublic || false}
				onCheckedChange={async (value) => {
					await mutateOk(
						zero.mutate(
							mutators.workspaces.update({
								id: workspace.id,
								helpCenterPublic: value,
							}),
						),
					)
					toast.success({
						title: `Docs ${!workspace.helpCenterPublic ? "are now public" : "are now hidden"}`,
					})
					analytics.track("toggle-help-center-enabled", {
						value,
						enabled: true,
					})
				}}
				title="Docs"
				description="Make your docs available on a public domain."
				link="docs"
			/>

			{tabsLoaded && (
				<>
					<SettingsSection title="API reference" className={dimmedClassName} />
					<SettingsContainer className={cn("mt-3", dimmedClassName)}>
						{apiReferenceTab ? (
							<ApiReferenceSettingsRow
								tabId={apiReferenceTab.id}
								name={apiReferenceTab.name}
							/>
						) : (
							<SettingItem
								title={
									<span className="flex items-center gap-2">
										OpenAPI spec
										<NewBadge />
									</span>
								}
								description="Create endpoint pages and keep them in sync."
								action={
									<Button
										className="flex-none"
										Icon={Plus}
										onClick={() => setNewApiReferenceOpen(true)}
									>
										Add spec
									</Button>
								}
							/>
						)}
					</SettingsContainer>
				</>
			)}
			{/* Outside the row, which drops out as soon as the new reference syncs. */}
			<NewApiReferenceDialog />

			<SettingsSection title="Automatic drafts" className={dimmedClassName} />
			<SettingsContainer className={cn("mt-3", dimmedClassName)}>
				<SettingItem
					title="Self-updating docs"
					description="Draft articles from merged PRs and Linear issues."
					action={
						<Switch
							name="autoProposeDocsUpdates"
							label={null}
							checked={workspace.autoProposeDocsUpdates ?? false}
							onCheckedChange={async (value) => {
								await mutateOk(
									zero.mutate(
										mutators.workspaces.update({
											id: workspace.id,
											autoProposeDocsUpdates: value,
										}),
									),
								)
								analytics.track("toggle-self-updating-docs", { value })
							}}
						/>
					}
				/>
				{!githubConnected && (
					<SettingItem
						className="pt-3"
						title="GitHub"
						description="Needed to draft from merged PRs."
						action={
							<Button
								className="flex-none"
								Icon={GitHubIcon}
								onClick={() =>
									void navigate({ to: "/settings/integrations/github" })
								}
							>
								Connect
							</Button>
						}
					/>
				)}
				{workspace.autoProposeDocsUpdates && (
					<SettingItem
						className="pt-3"
						title={DOCS_PROPOSAL_CADENCE_LABEL}
						description={DOCS_PROPOSAL_CADENCE_DESCRIPTION}
						action={
							<Select
								name="docsProposalCadence"
								showErrorMessage={false}
								options={DOCS_PROPOSAL_CADENCE_OPTIONS}
								value={workspace.docsProposalCadence ?? "DAILY"}
								onChange={(value) => void saveCadence(value)}
								className="w-44"
								selectClassName="dark:bg-gray-700/70"
								popoverAlign="end"
							/>
						}
					/>
				)}
				{workspace.autoProposeDocsUpdates && (
					<SettingItem
						className="pt-3"
						title="Auto-publish drafts"
						description="An AI checks each draft against your code, then publishes what it confirms. The rest wait for you."
						action={
							<Switch
								name="docsAutoMergeEnabled"
								label={null}
								checked={workspace.docsAutoMergeEnabled ?? false}
								onCheckedChange={async (value) => {
									await mutateOk(
										zero.mutate(
											mutators.workspaces.update({
												id: workspace.id,
												docsAutoMergeEnabled: value,
											}),
										),
									)
									analytics.track("toggle-docs-auto-merge", { value })
								}}
							/>
						}
					/>
				)}
				{workspace.autoProposeDocsUpdates && (
					<div className="flex flex-col gap-3 pt-3">
						<SettingItem
							title="Writing instructions"
							description="Tell the AI how to write your docs."
						/>
						<CollapsiblePromptTextarea
							name="docsCustomPrompt"
							aria-label="Writing instructions"
							placeholder="e.g. Write in second person, open every article with a one-sentence summary, and skip internal-only flags."
							value={docsPrompt.value}
							minRows={3}
							onChange={(e) => docsPrompt.onChange(e.target.value)}
							onBlur={docsPrompt.onBlur}
						/>
					</div>
				)}
			</SettingsContainer>

			<SettingsSection title="Navigation" className={dimmedClassName} />
			<SettingsContainer className={cn("mt-3", dimmedClassName)}>
				<SettingItem
					title="Tab style"
					description="How readers switch between docs tabs. Nothing shows with a single tab."
					action={
						<Select
							name="helpCenterNavStyle"
							showErrorMessage={false}
							options={DOCS_TAB_STYLE_OPTIONS}
							value={workspace.helpCenterNavStyle ?? "BAR"}
							onChange={(value) => void saveTabStyle(value)}
							className="w-52"
							selectClassName="dark:bg-gray-700/70"
							popoverAlign="end"
						/>
					}
				/>
			</SettingsContainer>

			<SettingsSection title="Reader AI" className={dimmedClassName} />
			<SettingsContainer className={cn("mt-3", dimmedClassName)}>
				<SettingItem
					title="AI chat"
					description="Let readers ask questions about your docs."
					action={
						<Switch
							name="logoLink"
							label={null}
							checked={workspace.aiChatSettings?.isEnabled}
							onCheckedChange={async (value) => {
								const settings = workspace.aiChatSettings
								if (!settings) {
									const id = generateId()
									await mutateOk(
										zero.mutate(
											mutators.aiChatSettings.create({
												id,
												isEnabled: true,
												workspaceId: workspace.id,
												exampleQuestions: docsDefaultExampleQuestions,
												welcomeMessage: defaultAiChatWelcomeMessage(
													workspace.name,
												),
											}),
										),
									)
								} else {
									await mutateOk(
										zero.mutate(
											mutators.aiChatSettings.update({
												id: settings.id,
												isEnabled: value,
											}),
										),
									)
								}

								analytics.track("toggle-knowledge-base-ai-chat", {
									value,
								})
							}}
						/>
					}
				/>
				<SettingItem
					className="pt-3"
					title="AI shortcuts"
					description="Show Open in ChatGPT and Open in Claude."
					action={
						<Switch
							name="helpCenterShowAiActions"
							label={null}
							checked={workspace.helpCenterShowAiActions ?? true}
							onCheckedChange={async (value) => {
								await mutateOk(
									zero.mutate(
										mutators.workspaces.update({
											id: workspace.id,
											helpCenterShowAiActions: value,
										}),
									),
								)
								analytics.track("toggle-docs-ai-actions", { value })
							}}
						/>
					}
				/>
			</SettingsContainer>

			<PortalSettingsCard dimmed={!workspace.helpCenterPublic} />
		</>
	)
}
